// Validates that a user-supplied path is a safe internal redirect target.
// Prevents open-redirect attacks by rejecting protocol-relative URLs,
// absolute external URLs, and anything that does not start with a single "/".
// Used by signin/signup flows that accept a ?redirectTo= query param.

const DEFAULT_SAFE_PATH = '/dashboard';

// Browsers STRIP tab (U+0009), LF (U+000A) and CR (U+000D) from a URL before resolving
// it (WHATWG URL parsing). So "/\t/evil.com" passed the prefix checks below — it does
// start with a single "/" — while the browser actually navigated to "//evil.com", a
// protocol-relative URL pointing at an external origin. That was #1785.
//
// Reject rather than strip-then-check: stripping would silently redirect somewhere other
// than what was requested. No legitimate internal path contains a control character, so
// rejecting fails closed to the caller's fallback. The class covers C0 controls plus DEL,
// catching the strippable trio and anything adjacent to it.
// eslint-disable-next-line no-control-regex
const CONTROL_CHARACTERS = /[\u0000-\u001F\u007F]/;

export function validateInternalPath(
  raw: string | null | undefined,
  fallback: string = DEFAULT_SAFE_PATH
): string {
  if (!raw) return fallback;

  let decoded: string;
  try {
    decoded = decodeURIComponent(raw);
  } catch {
    return fallback;
  }

  // Test the DECODED form: "%09" decodes to a tab, so an encoded payload still reaches
  // the browser as the same strippable character.
  if (CONTROL_CHARACTERS.test(decoded)) return fallback;

  if (!decoded.startsWith('/')) return fallback;
  if (decoded.startsWith('//')) return fallback;
  if (decoded.startsWith('/\\')) return fallback;

  return decoded;
}
